import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  Avatar,
  Badge,
  CadDocIcon,
  Divider,
  DocumentsIcon,
  EtcDocIcon,
  ExcelDocIcon,
  Grid,
  GridItem,
  HangulDocIcon,
  InfoCard,
  PdfDocIcon,
  PictureDocIcon,
  PowerpointDocIcon,
  ProgramIcon,
  TxtDocIcon,
  Typography,
  useAlert,
  useToast,
  VideoDocIcon,
  WordDocIcon,
  ZipDocIcon,
} from 'fasoo-ui-component-next';

import { ApprovalDetailEntity } from 'src/api/user/approvalApi';
import { DecryptApprovalDetailEntity } from 'src/api/user/decryptApprovalApi';
import { EncMailApprovalDetailEntity } from 'src/api/user/encMailApprovalApi';
import { ExchangeFullApprovalDetailEntity } from 'src/api/user/exchangeFullApprovalApi';
import { ExchangeOfflineApprovalDetailEntity } from 'src/api/user/exchangeOfflineApprovalApi';
import { PrintClientApprovalDetailEntity } from 'src/api/user/printClientApprovalApi';
import { PrintFileApprovalDetailEntity } from 'src/api/user/printFileApprovalApi';
import { PrintPeriodApprovalDetailEntity } from 'src/api/user/printPeriodApprovalApi';
import { ProvisionalApprovalDetailEtity } from 'src/api/user/provisionalApprovalApi';
import { ScanExemptApprovalDetailEntity } from 'src/api/user/scanExemptApprovalApi';
import { ScanLedgerApprovalDetailEntity } from 'src/api/user/scanLedgerApprovalApi';
import { ScreenApprovalDetailEntity } from 'src/api/user/screenApprovalApi';
import { TakeOutApprovalDetailEntity } from 'src/api/user/takeOutApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePreventScrollStore from 'src/hooks/usePreventScroll';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { dateToStringByFormat, stringToDate } from 'src/shared/utils/localDateTime';

import DecryptDetail from './DecryptDetail';
import EncMailDetail from './EncMailDetail';
import ExceptScreenDetail from './ExceptScreenDetail';
import ExchangeFullDetail from './ExchangeFullDetail';
import ExchangeOfflineDetail from './ExchangeOfflineDetail';
import PcTakeOutDetail from './PcTakeOutDetail';
import PrintClientDetail from './PrintClientDetail';
import PrintFileDetail from './PrintFileDetail';
import PrintPeriodPolicyDetail from './PrintPeriodPolicyDetail';
import ProvisionalDetail from './ProvisionalDetail';
import ScanExemptDetail from './ScanExemptDetail';
import ScanLedgerDetail from './ScanLedgerDetail';

import './index.scss';

// eslint-disable-next-line no-shadow
export enum SensitiveStatusType {
  DETECTED = 'DETECTED',
  NONE = 'NONE',
  ERROR = 'ERROR',
  NOTARGET = 'NOTARGET',
}

export const DETECTED_STATUS_LIST: { fst: string[]; snd: string[] } = {
  fst: [SensitiveStatusType.DETECTED, SensitiveStatusType.NONE, SensitiveStatusType.NOTARGET],
  snd: [SensitiveStatusType.ERROR],
};

export const FileIcon = (fileName: string): React.JSX.Element => {
  const curFileName = fileName;
  let fileIcon: React.JSX.Element = <EtcDocIcon />;
  const fileExtStartIdx = curFileName.lastIndexOf('.');
  if (fileExtStartIdx === -1) {
    return fileIcon;
  }
  const fileExt: string = curFileName.substring(curFileName.lastIndexOf('.') + 1);

  if (['doc', 'docx'].includes(fileExt)) {
    fileIcon = <WordDocIcon />;
  } else if (['ppt', 'pptx'].includes(fileExt)) {
    fileIcon = <PowerpointDocIcon />;
  } else if (['pdf'].includes(fileExt)) {
    fileIcon = <PdfDocIcon />;
  } else if (['xls', 'xlsx'].includes(fileExt)) {
    fileIcon = <ExcelDocIcon />;
  } else if (['hwp', 'hwpx'].includes(fileExt)) {
    fileIcon = <HangulDocIcon />;
  } else if (['avi', 'flv', 'mkv', 'mov', 'mp4'].includes(fileExt)) {
    fileIcon = <VideoDocIcon />;
  } else if (['bmp', 'gif', 'jpg', 'jpeg', 'png', 'pad', 'pic', 'raw', 'tiff'].includes(fileExt)) {
    fileIcon = <PictureDocIcon />;
  } else if (['zip'].includes(fileExt)) {
    fileIcon = <ZipDocIcon />;
  } else if (['txt'].includes(fileExt)) {
    fileIcon = <TxtDocIcon />;
  } else if (['exe'].includes(fileExt)) {
    fileIcon = <ProgramIcon />;
  } else if (['cad'].includes(fileExt)) {
    fileIcon = <CadDocIcon />;
  }

  return fileIcon;
};

export interface ShowElementProps extends CommonProps {
  title?: string | React.ReactElement;
  subTitle?: string;
  content?: string | React.ReactElement;
  subTitleSize?: 'sm' | 'md';
  titleStyle?: {
    padding?: string;
  };
  subtitleStyle?: {
    padding?: string;
  };
  contentStyle?: {
    padding?: string;
  };
}

export const ShowLineContent: React.FC<ShowElementProps> = ({
  children,
  classes,
  title,
  subTitle,
  content,
  subTitleSize = 'md',
  titleStyle,
  subtitleStyle,
  contentStyle,
}) => {
  return (
    <div className={classNames('line-content', classes)}>
      {children || (
        <Grid spacing={0}>
          {!title || (
            <GridItem
              classes={classNames('line-content-title')}
              xs={12}
              padding={titleStyle && titleStyle.padding ? titleStyle.padding : '16px 0px 0px 0px'}
            >
              <Typography variant="h3">{title}</Typography>
            </GridItem>
          )}
          {!subTitle || (
            <GridItem
              classes={classNames('line-content-sub-title', {
                'fs-12': subTitleSize === 'sm',
              })}
              xs={12}
              padding={
                subtitleStyle && subtitleStyle.padding
                  ? subtitleStyle.padding
                  : title
                  ? '8px 0px 0px 0px'
                  : '16px 0px 0px 0px'
              }
            >
              <Typography
                classes={classNames({
                  'fs-12': subTitleSize === 'sm',
                })}
                variant="p"
                color="secondary"
              >
                {subTitle}
              </Typography>
            </GridItem>
          )}
          {!content || (
            <GridItem
              classes="line-content-content"
              xs={12}
              padding={
                contentStyle && contentStyle.padding
                  ? contentStyle.padding
                  : title || subTitle
                  ? '8px 0px 0px 0px'
                  : '16px 0px 0px 0px'
              }
            >
              {content}
            </GridItem>
          )}
        </Grid>
      )}
    </div>
  );
};

export interface ApprovalUserInfoDto {
  userId: string; // 승인자 ID
  userName: string; // 승인자 이름
  positionName: string; //
  deptName: string;
  step?: number;
  approvalStatus?: string; // 승인 상태
  approvalRemark?: string;
  actorIp?: string;
  approvalDate?: number;

  // 대리승인 & 관리자 긴급 결재 항목 체크
  method?: string;
  otherDeptCode?: string | null;
  otherDeptName?: string | null;
  otherPositionCode?: string | null;
  otherPositionName?: string | null;
  otherRoleCode?: string | null;
  otherRoleName?: string | null;
  otherUserId?: string | null;
  otherUserName?: string | null;
}

export interface UserInfoProps extends CommonProps {
  step?: number;
  type: 'request' | 'approver';
  userInfo: ApprovalUserInfoDto;
  // userCard: React.ReactElement;
  dateStr: string | null;
  remark: string | null;
  submission?: string;
}

export const UserDetail: React.FC<UserInfoProps> = ({
  step,
  type = 'request',
  userInfo,
  dateStr,
  remark,
  submission,
  // key,
}) => {
  const { alert, confirm } = useAlert();
  const toast = useToast();
  const { t } = useTranslation(['userApproval']);

  // console.log('step: ', step);
  return (
    <div className="approver-detail-root">
      {/* {step !== undefined && (
        <> */}
      {step !== 1 && step !== undefined && <Divider />}

      {type !== 'request' && (
        <span className="step-info">
          {t('userApproval:stepWithNum', { step: step !== undefined ? step : 1 })}
        </span>
      )}

      {userInfo.approvalStatus && (
        <span className={`approval-status approval-status-${userInfo.approvalStatus}`}>
          {t(`userApproval:approvalDetailStatus.${userInfo.approvalStatus}`)}
        </span>
      )}
      {/* </>
      )} */}
      <InfoCard
        classes="user-card-root"
        startArea={<Avatar name={userInfo.userName} colorKey={userInfo.userId} />}
        title={`${userInfo.userName} (${userInfo.userId})`}
        description={
          <p className="user-desc">
            {userInfo.positionName} / {userInfo.deptName}
            {userInfo.method !== 'PROXY' &&
              userInfo.method !== 'ADMIN' &&
              userInfo.method !== 'URGENCY' &&
              userInfo.actorIp !== undefined &&
              ` · ${userInfo.actorIp}`}
          </p>
        }
      />
      {userInfo.method !== undefined &&
        (userInfo.method === 'PROXY' ||
          userInfo.method === 'ADMIN' ||
          userInfo.method === 'URGENCY') && (
          <ShowLineContent
            classes="detail-sub-info mt-12"
            subTitle={t(`userApproval:${userInfo.method.toLowerCase()}`)}
          />
        )}

      {userInfo.method !== undefined &&
        userInfo.otherDeptCode !== undefined &&
        userInfo.otherDeptCode !== null &&
        userInfo.otherDeptName !== undefined &&
        userInfo.otherDeptName !== null &&
        userInfo.otherPositionCode !== undefined &&
        userInfo.otherPositionCode !== null &&
        userInfo.otherPositionName !== undefined &&
        userInfo.otherPositionName !== null &&
        userInfo.otherRoleCode !== undefined &&
        userInfo.otherRoleCode !== null &&
        userInfo.otherRoleName !== undefined &&
        userInfo.otherRoleName !== null &&
        userInfo.otherUserId !== undefined &&
        userInfo.otherUserId !== null &&
        userInfo.otherUserName !== undefined &&
        userInfo.otherUserName !== null &&
        (userInfo.method === 'PROXY' || userInfo.method === 'URGENCY') && (
          <InfoCard
            classes="user-card-root"
            startArea={
              <Avatar
                name={userInfo.otherUserName}
                colorKey={userInfo.otherUserId}
                isManager={userInfo.method === 'URGENCY'}
              />
            }
            title={`${userInfo.otherUserName} (${userInfo.otherUserId})`}
            description={
              <p className="user-desc">
                {userInfo.otherPositionName} / {userInfo.otherDeptName}
                {userInfo.actorIp !== undefined && ` · ${userInfo.actorIp}`}
              </p>
            }
            // endArea={
            //   userInfo.approvalStatus !== undefined && (
            //     <div className={`text-status-root approval-status-${userInfo.approvalStatus}`}>
            //       {t(`userApproval:approvalDetailStatus.${userInfo.approvalStatus}`)}
            //     </div>
            //   )
            // }
          />
        )}
      {dateStr !== null && (
        <ShowLineContent
          classes="detail-sub-info"
          subTitle={type === 'request' ? t('userApproval:applyDate') : t('userApproval:date')}
          content={dateStr}
        />
      )}
      {submission && (
        <ShowLineContent
          classes="detail-sub-info detail-submission-info"
          subTitle={t('userApproval:submission')}
          content={submission}
        />
      )}
      {remark && (
        <ShowLineContent
          classes="detail-sub-info detail-remark-info"
          subTitle={
            type === 'request' ? t('userApproval:reason') : t('userApproval:approverReason')
          }
          content={remark}
        />
      )}
    </div>
  );
};

export interface DownloadHistoryInfoProps extends CommonProps {
  fileName: string;
  updatedTsStr: string;
  userIpText: string;
  userName: string;
  userId: string;
  action: string; // 'DOWNLOADDECRYPT' | 'DOWNLOADORIGIN'
  adminRemark: string | null;
  key?: React.Key;
}

export const DownloadHistoryInfo: React.FC<DownloadHistoryInfoProps> = ({
  fileName,
  updatedTsStr,
  userIpText,
  userName,
  userId,
  action,
  adminRemark,
  key,
}) => {
  const { alert, confirm } = useAlert();
  const toast = useToast();
  const { t } = useTranslation(['userApproval']);

  return (
    <div key={key}>
      <InfoCard
        classes="download-history-info-root"
        startArea={FileIcon(fileName)}
        title={fileName}
        description={
          <>
            <div className="download-history-info-root__item__descript">
              {getFasooUiComponentLanguage() === 'ko'
                ? updatedTsStr
                : dateToStringByFormat(stringToDate(updatedTsStr), 'MM/DD/YYYY HH:mm')}{' '}
              · {userIpText}
            </div>
            <div className="download-history-info-root__item__descript">
              {userName}({userId}) · {t(`userApproval:fileDetails.history.${action}`)}
            </div>
          </>
        }
        ellipsisContent
      />
    </div>
  );
};

export interface LinkProps extends CommonProps {
  href?: string;
  text?: string;
  isDownload?: boolean;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
}

export const Link: React.FC<LinkProps> = ({ children, text, href, isDownload = true, onClick }) => {
  const defaultOnClick: () => void = () => {
    window.location.href = href || './#';
    // console.log(href);
  };
  return (
    <div
      className={classNames('link-root', `link__button--${isDownload ? 'enable' : 'disable'}`)}
      onClick={onClick || defaultOnClick}
    >
      {text}
      {children}
    </div>
  );
};
export interface ShowHumanInfoProps extends CommonProps {
  userId: string;
  userName: string;
  positionName: string;
  deptName: string;
  actorIp?: string;
  status?: 'APPLY' | 'PROGRESS' | 'APPROVAL' | 'REJECT' | 'CANCEL';
}

export const ShowHumanInfo: React.FC<ShowHumanInfoProps> = ({
  userId,
  userName,
  positionName,
  deptName,
  actorIp,
  status,
}) => {
  const { alert, confirm } = useAlert();
  const toast = useToast();
  const { t } = useTranslation(['userApproval']);

  const ShowBadge: (
    // eslint-disable-next-line no-shadow
    status: 'APPLY' | 'PROGRESS' | 'APPROVAL' | 'REJECT' | 'CANCEL'
    // eslint-disable-next-line react/no-unstable-nested-components
  ) => React.JSX.Element = () => {
    let color: 'green' | 'orange' | 'red' | 'gray' = 'gray';
    if (status === 'PROGRESS') {
      color = 'orange';
    } else if (status === 'APPROVAL') {
      color = 'green';
    } else if (status === 'REJECT') {
      color = 'red';
    }
    return <Badge color={color} text={t(`userApproval:status.${status}`)} />;
  };

  return (
    <Grid spacing={5}>
      <GridItem xs={2} alignSelf="center">
        <Avatar name={userName} colorKey={userId} />
      </GridItem>
      <GridItem xs={8}>
        <Typography variant="p">{`${userName} (${userId})`}</Typography>
        <div style={{ height: '4px', width: '1px' }} />
        <Typography variant="p" color="secondary">
          {positionName} / {deptName}
        </Typography>
        <div style={{ height: '4px', width: '1px' }} />
        <Typography variant="p" color="secondary">
          {actorIp}
        </Typography>
      </GridItem>
      {!status || (
        <GridItem xs={2} alignSelf="center">
          {ShowBadge(status)}
        </GridItem>
      )}
    </Grid>
  );
};

export interface OnClickFileDownloadProps extends CommonProps {
  downloadReqDto: { approvalNo: string; fileUuid: string; fileName: string; userId: string }[];
  downloadType: 'zip' | 'multi' | 'one';
  status: 'APPLY' | 'PROGRESS' | 'APPROVAL' | 'REJECT' | 'CANCEL' | string;
  method?:
    | 'PRE'
    | 'POST'
    | 'SELF'
    | 'NOT_ALLOW'
    | 'SYSTEM'
    | 'URNGENCY'
    | 'PROXY'
    | 'NORMAL'
    | 'ARBITRARYDECISION'
    | 'PARALLEL'
    | string;
  adminRemark: string | null;
}

export type AllApprovalDetailEntity =
  | DecryptApprovalDetailEntity
  | TakeOutApprovalDetailEntity
  | ScreenApprovalDetailEntity
  | ProvisionalApprovalDetailEtity
  | PrintPeriodApprovalDetailEntity
  | PrintFileApprovalDetailEntity
  | PrintClientApprovalDetailEntity
  | ScanExemptApprovalDetailEntity
  | ScanLedgerApprovalDetailEntity
  | EncMailApprovalDetailEntity
  | ExchangeOfflineApprovalDetailEntity
  | ExchangeFullApprovalDetailEntity;

export type ApprovalDetailInfoPageType =
  | 'userApprovalComplete'
  | 'managerFile'
  | 'managerLog'
  | 'userEncmailApprovalComplete'
  | 'others';

// 암호화해제의 경우 파일 다운로드 상태 확인을 위해 DecryptDetail Component안에서 상세정보를 받아오록 변경함
//  => 다른 신청건과 다르게 DetailEntity를 받아오는 것이 아닌 approvalNo를 받아오도록 변경
interface ApprovalDetailInfoProps extends CommonProps {
  open: boolean;
  selectItem: boolean;
  type: string | null;
  ResetDetail: () => void;
  decryptApprovalDetailInfo?: DecryptApprovalDetailEntity | null;
  cancelApprovalChildren?: React.ReactNode;
  takeoutApprovalDetailInfo?: TakeOutApprovalDetailEntity | null;
  screenApprovalDetailInfo?: ScreenApprovalDetailEntity | null;
  provisionalApprovalDetailInfo?: ProvisionalApprovalDetailEtity | null;
  printperiodpolicyApprovalDetailInfo?: PrintPeriodApprovalDetailEntity | null;
  printFileApprovalDetailInfo?: PrintFileApprovalDetailEntity | null;
  printClientApprovalDetailInfo?: PrintClientApprovalDetailEntity | null;
  scanExemptApprovalDetailInfo?: ScanExemptApprovalDetailEntity | null;
  scanLedgerApprovalDetailInfo?: ScanLedgerApprovalDetailEntity | null;
  encMailApprovalDetailInfo?: EncMailApprovalDetailEntity | null;
  exchangeFullApprovalDetailInfo?: ExchangeFullApprovalDetailEntity | null;
  exchangeOfflineApprovalDetailInfo?: ExchangeOfflineApprovalDetailEntity | null;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

const ApprovalDetailInfo: React.FC<ApprovalDetailInfoProps> = ({
  children,
  open = false,
  selectItem = false,
  type = null,
  ResetDetail,
  onCloseDetail,
  decryptApprovalDetailInfo,
  cancelApprovalChildren,
  takeoutApprovalDetailInfo,
  screenApprovalDetailInfo,
  provisionalApprovalDetailInfo,
  printperiodpolicyApprovalDetailInfo,
  printFileApprovalDetailInfo,
  printClientApprovalDetailInfo,
  scanExemptApprovalDetailInfo,
  scanLedgerApprovalDetailInfo,
  encMailApprovalDetailInfo,
  exchangeFullApprovalDetailInfo,
  exchangeOfflineApprovalDetailInfo,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['userApproval']);
  const { startPreventScroll, stopPreventScroll } = usePreventScrollStore();

  const typeVariant = ((): ApprovalDetailInfoPageType => {
    if (!type) {
      return 'others';
    }
    if (type.includes('User')) {
      return 'userApprovalComplete';
    }
    if (type.includes('ManagerFile')) {
      return 'managerFile';
    }
    if (type.includes('ManagerLog')) {
      return 'managerLog';
    }
    return 'others';
  })();
  useEffect(() => {
    if (selectItem) {
      startPreventScroll();
    }
    return () => {
      stopPreventScroll();
    };
  }, [selectItem]);

  return (
    <div className={classNames('details-info-root', !open && 'details-info-hide')}>
      {selectItem ? (
        <>
          {!(type && type.includes('decryptApproval')) || !decryptApprovalDetailInfo || (
            <DecryptDetail
              onCloseDetail={onCloseDetail}
              decryptDetail={decryptApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              pageType={typeVariant}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}

          {!(type === 'takeoutApproval') || !takeoutApprovalDetailInfo || (
            <PcTakeOutDetail
              onCloseDetail={onCloseDetail}
              pcTakeOutApproval={takeoutApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}

          {!(type === 'screenApproval') || !screenApprovalDetailInfo || (
            <ExceptScreenDetail
              onCloseDetail={onCloseDetail}
              screenApproval={screenApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}

          {!(type === 'provisionalApproval') || !provisionalApprovalDetailInfo || (
            <ProvisionalDetail
              onCloseDetail={onCloseDetail}
              provisionalApprovalDetail={provisionalApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
              pageType={typeVariant}
            />
          )}
          {!(type === 'printpolicyApproval') || !printperiodpolicyApprovalDetailInfo || (
            <PrintPeriodPolicyDetail
              onCloseDetail={onCloseDetail}
              printperiodpolicyApprovalDetail={printperiodpolicyApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type === 'printfileApproval' || type === 'printfileApprovalUser') ||
            !printFileApprovalDetailInfo || (
              <PrintFileDetail
                onCloseDetail={onCloseDetail}
                printFileApprovalDetail={printFileApprovalDetailInfo}
                ResetDetail={ResetDetail}
                cancelApprovalChildren={cancelApprovalChildren}
                pageType={typeVariant}
                isComplete={isComplete}
                selectedApproval={selectedApproval}
              />
            )}
          {!(type === 'printclientApproval') || !printClientApprovalDetailInfo || (
            <PrintClientDetail
              onCloseDetail={onCloseDetail}
              printClientApprovalDetail={printClientApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type === 'scanexemptApproval') || !scanExemptApprovalDetailInfo || (
            <ScanExemptDetail
              onCloseDetail={onCloseDetail}
              scanExemptApproval={scanExemptApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type === 'scanledgerApproval') || !scanLedgerApprovalDetailInfo || (
            <ScanLedgerDetail
              onCloseDetail={onCloseDetail}
              scanLedgerApproval={scanLedgerApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type && type.includes('encmailApproval')) || !encMailApprovalDetailInfo || (
            <EncMailDetail
              onCloseDetail={onCloseDetail}
              encMailApproval={encMailApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              pageType={typeVariant}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type && type.includes('exchangefullApproval')) || !exchangeFullApprovalDetailInfo || (
            <ExchangeFullDetail
              onCloseDetail={onCloseDetail}
              exchangeFullApproval={exchangeFullApprovalDetailInfo}
              ResetDetail={ResetDetail}
              cancelApprovalChildren={cancelApprovalChildren}
              pageType={typeVariant}
              isComplete={isComplete}
              selectedApproval={selectedApproval}
            />
          )}
          {!(type && type.includes('exchangeofflineApproval')) ||
            !exchangeOfflineApprovalDetailInfo || (
              <ExchangeOfflineDetail
                onCloseDetail={onCloseDetail}
                exchangeOfflineDetail={exchangeOfflineApprovalDetailInfo}
                ResetDetail={ResetDetail}
                cancelApprovalChildren={cancelApprovalChildren}
                pageType={typeVariant}
                isComplete={isComplete}
                selectedApproval={selectedApproval}
              />
            )}

          {children}
        </>
      ) : (
        <div className={classNames(`details-card`)}>
          <div style={{ height: '32px' }} />
          <DocumentsIcon />
          <div style={{ height: '32px' }} />
          <Typography variant="p" color="secondary">
            {t('userApproval:message.noneSelectApproval.first')}
          </Typography>
          <div style={{ height: '4px' }} />
          <Typography variant="p" color="secondary">
            {t('userApproval:message.noneSelectApproval.second')}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ApprovalDetailInfo;
